import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, DatePicker, message, Spin, Tag, Space, Select } from 'antd';
import { fetchQuotations, createQuotation, updateQuotation, deleteQuotation, sendQuotationEmail } from '../../features/Projects/projectSlice';
import dayjs from 'dayjs';

const QuotationManagement = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { quotations, loading: quotationsLoading } = useSelector((state) => state.projects);
  const { projects } = useSelector((state) => state.projects); // Fetch projects from Redux store
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingQuotation, setEditingQuotation] = useState(null);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchQuotations(projectId)).catch(() => {
        message.error('Failed to fetch quotations.');
      });
    }
  }, [dispatch, projectId]);

  // Find the current project using the projectId from the URL
  const currentProject = projects.find((project) => project.id === parseInt(projectId));

  // Get the client's email from the project's user relationship
  const clientEmail = currentProject?.user?.email;


  const handleAdd = () => {
    setEditingQuotation(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (quotation) => {
    setEditingQuotation(quotation);
    form.setFieldsValue({
      ...quotation,
      deadline: quotation.deadline ? dayjs(quotation.deadline) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (quotationId) => {
    dispatch(deleteQuotation({ projectId, quotationId }))
      .unwrap()
      .then(() => message.success('Quotation deleted successfully'))
      .catch(() => message.error('Failed to delete quotation'));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const quotationData = {
        ...values,
        deadline: values.deadline ? values.deadline.format('YYYY-MM-DD') : null,
      };

      if (editingQuotation) {
        await dispatch(updateQuotation({ projectId, quotationId: editingQuotation.id, data: quotationData })).unwrap();
        message.success('Quotation updated successfully');
      } else {
        await dispatch(createQuotation({ projectId, data: quotationData })).unwrap();
        message.success('Quotation created successfully');
      }

      setIsModalVisible(false);
    } catch (error) {
      message.error('Failed to submit quotation');
    }
  };

  const handleSendEmail = (quotationId) => {
    if (!clientEmail) {
      message.error('Client email not found.');
      return;
    }

    Modal.confirm({
      title: 'Send Quotation to Client',
      content: `Are you sure you want to send this quotation to ${clientEmail}?`,
      onOk: async () => {
        try {
          await dispatch(sendQuotationEmail({ projectId, quotationId, clientEmail })).unwrap();
          message.success('Quotation sent successfully');
        } catch (error) {
          message.error('Failed to send quotation');
        }
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Total Quotation',
      dataIndex: 'total_quotation',
      key: 'total_quotation',
    },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
    },
    {
      title: 'Rejection Reason',
      dataIndex: 'rejection_reason',
      key: 'rejection_reason',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'accepted' ? 'green' : status === 'rejected' ? 'red' : 'orange'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>Delete</Button> */}
          <Button type="link" onClick={() => handleSendEmail(record.id)}>Send Email</Button>
        </Space>
      ),
    },
  ];

  const quotationData = quotations[projectId] || [];

  return (
    <div style={{ padding: '20px' }}>
      <Button onClick={() => navigate(-1)} style={{ marginBottom: '16px' }}>Back to Projects</Button>
      <h2>Quotation Management for Project ID: {projectId}</h2>
      <Button type="primary" onClick={handleAdd} style={{ marginBottom: '16px' }}>
        Add Quotation
      </Button>
      {quotationsLoading ? (
        <Spin />
      ) : (
        <Table dataSource={quotationData} columns={columns} rowKey="id" />
      )}

      <Modal
        title={editingQuotation ? 'Edit Quotation' : 'Add Quotation'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="total_quotation" label="Total Quotation" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="deadline" label="Deadline" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="accepted">Accepted</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuotationManagement;